import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { getMe } from "@/lib/socialpulseOidc";
import { VerifiedBadge, PageToolbar, usePageTranslation } from "@/components/PageToolbar";

const INTRO = { title: "Community", sub: "Theater memories, gateway films, restoration wishes — moderated discussion for every film culture. Be kind, mark spoilers, respect creators." };

export default function Community() {
  const [threads, setThreads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", author: "", category: "General Discussion" });

  const load = () => api.get("/forum/threads").then((r) => setThreads(r.data));
  useEffect(() => {
    load();
    getMe().then(setMe);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and message are required");
    await api.post("/forum/threads", form);
    toast.success("Discussion started!");
    setForm({ title: "", body: "", author: "", category: "General Discussion" });
    setShowForm(false);
    load();
  };

  const categories = ["General Discussion", "World Cinema", "Recommendations", "Theater Memories", "Preservation", "Education & Careers"];
  const blocks = useMemo(() => INTRO, []);
  const { lang, tr, busy } = usePageTranslation(blocks, "community-intro");

  return (
    <main data-testid="community-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <p className="eyebrow">Forums & Local Film Groups</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
        <div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1c1512]">{tr("title", INTRO.title)}</h1>
          <p className="text-[#6b5d51] mt-3 max-w-2xl">{tr("sub", INTRO.sub)}</p>
          <div className="mt-4">
            <PageToolbar speakText={`${tr("title", INTRO.title)}. ${tr("sub", INTRO.sub)}`} busy={busy} lang={lang} />
          </div>
        </div>
        <button
          data-testid="new-thread-button"
          onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 bg-[#7d1a1a] text-[#f6f1e7] px-5 py-2.5 font-medium hover:bg-[#5c1220] transition-colors"
        >
          {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showForm ? "Cancel" : "Start a Discussion"}
        </button>
      </div>

      {showForm && (
        <form data-testid="new-thread-form" onSubmit={submit} className="mt-8 bg-[#fbf8f1] border-2 border-[#1c1512] p-6 space-y-4">
          <input
            data-testid="thread-title-input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Discussion title"
            className="w-full bg-transparent border-b-2 border-[#d8ccb8] focus:border-[#7d1a1a] outline-none py-2 font-display text-xl"
          />
          <textarea
            data-testid="thread-body-input"
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="What's on your mind about cinema?"
            rows={4}
            className="w-full bg-transparent border border-[#d8ccb8] focus:border-[#7d1a1a] outline-none p-3 text-sm"
          />
          <div className="flex flex-wrap gap-3 items-center">
            {me ? (
              <span data-testid="posting-as-label" className="text-sm text-[#6b5d51]">
                Posting as <span className="font-medium text-[#1c1512]">{me.name}</span> <VerifiedBadge />
              </span>
            ) : (
              <input
                data-testid="thread-author-input"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                placeholder="Your name (optional)"
                className="bg-transparent border border-[#d8ccb8] focus:border-[#7d1a1a] outline-none px-3 py-2 text-sm"
              />
            )}
            <select
              data-testid="thread-category-select"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="bg-[#fbf8f1] border border-[#d8ccb8] outline-none px-3 py-2 text-sm cursor-pointer"
            >
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
            <button data-testid="thread-submit-button" type="submit" className="bg-[#1c1512] text-[#f6f1e7] px-5 py-2 font-medium hover:bg-[#7d1a1a] transition-colors">
              Post Discussion
            </button>
          </div>
        </form>
      )}

      <div className="mt-10 space-y-3">
        {threads.map((t) => (
          <Link key={t.id} to={`/community/${t.id}`} data-testid={`thread-card-${t.id}`} className="block bg-[#fbf8f1] border border-[#d8ccb8] p-5 hover:border-[#7d1a1a] transition-colors group">
            <div className="flex items-center gap-3">
              <span className="font-mono-label text-[10px] tracking-[0.18em] uppercase bg-[#efe7d7] px-2 py-1">{t.category}</span>
              <span className="font-mono-label text-[10px] text-[#8a7c6d]">{new Date(t.created).toLocaleDateString()}</span>
            </div>
            <h2 className="font-display font-semibold text-xl mt-2 group-hover:text-[#7d1a1a] transition-colors">{t.title}</h2>
            <div className="flex items-center gap-4 mt-2 text-sm text-[#6b5d51]">
              <span className="flex items-center gap-1.5">by {t.author} {t.verified && <VerifiedBadge />}</span>
              <span className="flex items-center gap-1"><MessageSquare className="w-4 h-4" /> {t.reply_count} replies</span>
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
}
